<template>
    <div class="relative flex flex-col">
        <div v-if="isLoading" class="h-[171px] flex w-full overflow-hidden">
            <div class="h-full flex py-4 px-3">
                <vue-content-loading
                    v-for="index in listDirection === 'horizontal' ? 2 : 1"
                    :key="index"
                    :height="90"
                    :width="210"
                    :speed="2"
                    class="mb-3"
                    primaryColor="#f3f3f3"
                    secondaryColor="#ecebeb"
                >
                    <rect rx="3" ry="3" width="96%" height="98%" />
                </vue-content-loading>
            </div>
        </div>
        <ul
            :class="{
                'horizontal-list py-4 px-3': listDirection === 'horizontal'
            }"
            v-else
        >
            <li
                v-for="voucher in vouchers"
                :key="voucher.uuid"
                class="cursor-pointer border-b border-dashed p-4"
                :class="{
                    'last:border-b-0': listDirection !== 'horizontal',
                    'ring-2 ring-brown-400 rounded-lg shadow-sm':
                        isRewardActive(voucher),
                    'opacity-75': !isRewardAvailableForCart(voucher)
                }"
            >
                <div
                    class="transition-scale flex w-full flex-row duration-300 sm:hover:scale-[101%]"
                >
                    <img
                        v-if="voucher.reward?.media?.value"
                        :src="`https://71f65abd77a8d66ef2e1.ucr.io/-/resize/208x/${voucher.reward.media.value}`"
                        class="h-[104px] w-[104px] shrink-0 rounded-md object-cover"
                        alt="reward"
                    />
                    <div class="flex flex-col pl-2 pr-4 py-2">
                        <p
                            class="mb-4 font-heading text-heading text-sm font-bold"
                            >{{ voucher.name }}</p
                        >
                        <p
                            v-if="voucher.expiration_date"
                            class="text-xs text-subheading mb-1.5"
                            >{{ $t('piggy.useBefore') }}
                            {{
                                $dayjs(voucher.expiration_date).format('Do MMM')
                            }}</p
                        >
                        <span
                            class="border-button-primary w-fit select-none text-button bg-button font-button clip-path-button rounded-button rounded-md p-2 text-xs font-bold shadow"
                            @click="toggleReward(voucher)"
                        >
                            {{
                                isRewardActive(voucher)
                                    ? $t('piggy.saveForNextTime')
                                    : $t('piggy.redeem')
                            }}</span
                        >
                    </div>
                </div>
            </li>
            <li
                v-for="collectableReward in collectableRewards"
                :key="collectableReward.id"
                class="cursor-pointer border-b border-dashed p-4"
                :class="{
                    'last:border-b-0': listDirection !== 'horizontal',
                    'ring-2 ring-brown-400 rounded-lg shadow-sm':
                        isRewardActive(collectableReward),
                    'opacity-75': !isRewardAvailableForCart(collectableReward)
                }"
            >
                <div
                    class="transition-scale flex w-full flex-row duration-300 sm:hover:scale-[101%]"
                >
                    <img
                        v-if="collectableReward.reward?.media?.value"
                        :src="`https://71f65abd77a8d66ef2e1.ucr.io/-/resize/208x/${collectableReward.reward.media.value}`"
                        class="h-[104px] w-[104px] shrink-0 rounded-md object-cover"
                        alt="reward"
                    />
                    <div class="flex flex-col px-4 py-2">
                        <p class="text-sm"
                            >{{ $t('piggy.pickupBefore') }}
                            {{
                                $dayjs(collectableReward.expires_at).format(
                                    'dddd, MMMM D, YYYY h:mm A'
                                )
                            }}</p
                        >
                        <p class="mb-4 font-heading text-base font-bold"
                            >{{ collectableReward.title }}
                            {{
                                `(-${collectableReward.reward.cost_price}%)`
                            }}</p
                        >
                        <span
                            class="border-button-primary w-fit text-button bg-button font-button clip-path-button rounded-button rounded-md p-2 text-xs font-bold shadow"
                            @click="toggleReward(collectableReward)"
                        >
                            {{
                                isRewardActive(collectableReward)
                                    ? $t('piggy.saveForNextTime')
                                    : $t('piggy.readyForPickup')
                            }}
                        </span>
                    </div>
                </div>
            </li>

            <li
                v-for="reward in rewards"
                :key="reward.id"
                class="cursor-pointer p-4"
                :class="{
                    'border-b border-dashed last:border-b-0':
                        listDirection === 'vertical',
                    'ring-2 ring-brown-400 rounded-lg shadow-sm':
                        isRewardActive(reward),
                    'opacity-75': !isRewardAvailableForCart(reward)
                }"
            >
                <div
                    class="transition-scale flex w-full flex-row duration-300 sm:hover:scale-[101%]"
                >
                    <img
                        v-if="reward.media?.value"
                        :src="`https://71f65abd77a8d66ef2e1.ucr.io/-/resize/208x/${reward.media?.value}`"
                        class="h-[104px] w-[104px] shrink-0 rounded-md object-cover"
                        alt="reward"
                    />
                    <div class="flex flex-col px-4 py-2">
                        <p
                            class="mb-4 text-heading font-heading text-sm font-bold"
                            >{{ reward.title }}</p
                        >
                        <p
                            v-if="reward.required_credits"
                            class="mb-1.5 text-xs text-subheading"
                            >{{ reward.required_credits }}
                            {{ $t('piggy.credits') }}</p
                        >
                        <span
                            v-if="
                                isCreditBalanceInsufficient(
                                    reward.required_credits
                                )
                            "
                            class="border-button-primary opacity-75 w-fit text-button bg-button font-button clip-path-button rounded-button rounded-md p-2 text-xs font-bold shadow"
                            >{{ $t('piggy.notEnough') }}
                            {{ piggyCreditName }}</span
                        >
                        <span
                            v-if="
                                contact.credit_balance &&
                                !isCreditBalanceInsufficient(
                                    reward.required_credits
                                )
                            "
                            class="border-button-primary w-fit text-button bg-button font-button clip-path-button rounded-button rounded-md p-2 text-xs font-bold shadow"
                            @click="toggleReward(reward)"
                        >
                            {{
                                isRewardActive(reward)
                                    ? $t('piggy.saveForNextTime')
                                    : $t('piggy.redeem')
                            }}
                        </span>
                        <span
                            v-if="
                                !contact.credit_balance &&
                                !isCreditBalanceInsufficient(
                                    reward.required_credits
                                )
                            "
                            class="border-button-primary w-fit text-button bg-button font-button clip-path-button rounded-button rounded-md p-2 text-xs font-bold shadow"
                            >{{ $t('piggy.signUpToClaim') }}</span
                        >
                    </div>
                </div>
            </li>
        </ul>
        <div
            v-if="noRewardsFound"
            class="flex h-[171px] items-center justify-center text-xl font-bold"
        >
            {{ $t('piggy.noRewardsAvailable') }}
        </div>
    </div>
</template>

<script>
import {
    findItemById,
    parseDiscountType,
    calculateDiscount,
    getProductsToDiscount,
    calculateProductPrice,
    updateRewardAvailability,
    getRewards,
    claimRewards
} from '@/services/piggyService';
import { debounce } from '@/helpers/debounce';
import VueContentLoading from 'vue-content-loading';
import { mapGetters, mapState, mapActions } from 'vuex';
import { getDiscountValue, getDiscountType } from '@/util/piggyHelper';

export default {
    name: 'PiggyRewardsList',
    components: {
        VueContentLoading
    },
    data() {
        return {
            rewards: [],
            collectableRewards: [],
            vouchers: [],
            contact: {},
            isLoading: false,
            noRewardsFound: false,
            discountType: {},
            reward: null
        };
    },
    props: {
        listDirection: {
            type: String,
            default: 'vertical',
            validator: function (value) {
                return ['vertical', 'horizontal'].indexOf(value) !== -1;
            }
        }
    },
    watch: {
        cartSubtotal: {
            handler: debounce(async function () {
                await this.getRewards();
            }, 300)
        }
    },
    created() {
        if (!this.isBillPayment) {
            this.getRewards();
            return;
        }

        this.rewards = this.billRewards;
        this.contact = this.billPiggyContact;
        this.checkRewardAvailability();
    },
    computed: {
        ...mapState({
            email: state => state.customer.email,
            piggySettings: state => state.venue.venue.piggySettings,
            billPiggyContact: state => state.bill.bill.contact,
            cartItems: state => state.cart.items,
            posProvider: state => state.venue.venue.posProvider,
            billId: state => state.bill.id,
            totalPiggyDiscount: state => state.cart.totalPiggyDiscount,
            rewardSelected: state => state.cart.piggyRewardSelected
        }),
        ...mapState('menu', {
            categories: state => state.categories
        }),
        ...mapGetters({
            venueValue: 'venue/venueValue',
            billRewards: 'bill/billRewards',
            billItems: 'bill/billItems',
            cartSubtotal: 'cart/cartSubtotal',
            venueProducts: 'venue/products',
            amountSelectedToPay: 'bill/amountSelected'
        }),
        accountId() {
            return this.venueValue('accountId');
        },
        venueId() {
            return this.venueValue('id');
        },
        piggyCreditName() {
            return this.piggySettings.creditUnitName || 'points';
        },
        isBillPayment() {
            return !!(this.billRewards && this.billRewards.length);
        },
        items() {
            return this.isBillPayment ? this.billItems : this.cartItems;
        }
    },
    methods: {
        ...mapActions({
            setPiggyProductDiscount: 'cart/SET_PIGGY_PRODUCT_DISCOUNT',
            cartTotalWithDiscount: 'cart/cartTotalWithDiscount',
            removeDiscount: 'cart/removeDiscount'
        }),
        isRewardAvailableForCart(reward) {
            return reward.isAvailable || false;
        },
        async claim(reward) {
            this.reward = reward;
            this.discountType = parseDiscountType(getDiscountType(this.reward));
            this.$store.commit('cart/CLEAR_PIGGY_DISCOUNT');

            if (
                (this.reward['has_been_collected'] === undefined &&
                    this.reward.pre_redeemable) ||
                this.billId
            ) {
                let payload = {
                    accountId: this.accountId,
                    rewardId: this.reward.uuid,
                    isPending: false,
                    piggyShopId: this.piggySettings.piggyShopId,
                    contactId: this.contact.uuid
                };

                payload = await this.handleBillReward(payload);

                const { data } = await claimRewards(payload);

                if (!this.billId) {
                    this.$notify({
                        group: 'PiggyRewards',
                        title: this.$t('piggy.rewardClaimedWithExpiry', {
                            expiryDate: this.$dayjs(data.expires_at).format(
                                'dddd, MMMM D, YYYY h:mm A'
                            )
                        })
                    });
                }
            } else {
                this.$store.commit(
                    'cart/SET_PIGGY_REWARD_SELECTED',
                    this.reward
                );
                this.$notify({
                    group: 'PiggyRewards',
                    title: this.$t('piggy.rewardClaimedAndAdded')
                });
                this.applyDiscount();
                this.removeDiscount();
            }

            this.$modal.hide('piggy-rewards-modal');
        },
        async getRewards() {
            this.isLoading = true;
            this.noRewardsFound = false;

            try {
                const params = {
                    email: this.email,
                    piggyShopId: this.piggySettings.piggyShopId,
                    accountId: this.accountId,
                    venueId: this.venueId
                };

                const { data } = await getRewards(params);

                if (!data.contact) {
                    this.rewards = data;
                } else {
                    this.rewards = data.rewards;
                    this.collectableRewards = data.collectableRewards;
                    this.vouchers = data.vouchers;
                    this.contact = data.contact;
                }
            } catch (error) {
                throw new Error(`API ${error}`);
            } finally {
                this.isLoading = false;

                if (!this.rewards.length && !this.vouchers.length) {
                    this.noRewardsFound = true;
                }

                this.checkRewardAvailability();
            }
        },
        checkRewardAvailability() {
            updateRewardAvailability(
                this.rewards,
                this.collectableRewards,
                this.vouchers,
                this.contact,
                this.items,
                this.categories,
                this.venueProducts,
                this.posProvider
            );
        },
        isCreditBalanceInsufficient(requiredCredits) {
            return this.contact?.credit_balance?.balance < requiredCredits;
        },
        applyDiscount() {
            if (
                this.discountType.type === 'basket_amount' ||
                this.discountType.type === 'basket_percentage'
            ) {
                this.applyBasketDiscount(this.discountType.type);
                return;
            }

            if (this.discountType.type === 'modifier_percent_base_single') {
                this.modifierPercentDiscount();
                return;
            }

            const product = findItemById(
                this.reward.selectedProduct,
                this.items
            );
            const discountValue = getDiscountValue(this.reward);
            const productsToDiscount = getProductsToDiscount(
                this.reward,
                product,
                this.discountType,
                this.items,
                this.categories,
                this.posProvider
            );

            productsToDiscount.forEach(product => {
                const { productPrice } = calculateProductPrice(
                    product,
                    product.quantity,
                    this.discountType
                );
                const discount = calculateDiscount(
                    productPrice,
                    discountValue,
                    this.discountType,
                    this.isBillPayment ? this.amountSelectedToPay : null
                );

                this.setPiggyProductDiscount({
                    id: product.id,
                    value: discount,
                    modifierIds: product.modifiers.map(modifier => modifier.id)
                });
            });

            this.cartTotalWithDiscount();
        },
        applyBasketDiscount(discountType) {
            const discountValue = getDiscountValue(this.reward);

            if (discountType === 'basket_amount') {
                this.setPiggyProductDiscount({
                    id: 'basket_amount',
                    value: Math.min(discountValue * 100, this.cartSubtotal),
                    modifierIds: []
                });
            }

            if (discountType === 'basket_percentage') {
                this.setPiggyProductDiscount({
                    id: 'basket_percentage',
                    value: this.cartSubtotal * (discountValue / 100),
                    modifierIds: []
                });
            }

            this.cartTotalWithDiscount();
        },
        modifierPercentDiscount() {
            const product = this.items.find(
                cartItem =>
                    cartItem.modifiers &&
                    cartItem.modifiers.some(
                        modifier => modifier.id === this.reward.selectedProduct
                    )
            );
            const modifier = product.modifiers.find(
                modifier => modifier.id === this.reward.selectedProduct
            );
            const discountValue = getDiscountValue(this.reward);

            this.setPiggyProductDiscount({
                id: product.id,
                value: Math.min(
                    modifier.price *
                        (modifier.quantity || 1) *
                        (discountValue / 100),
                    modifier.price * (modifier.quantity || 1)
                ),
                modifierIds: [modifier.id]
            });

            this.cartTotalWithDiscount();
        },
        async handleBillReward(payload) {
            if (!this.billId) {
                return payload;
            }

            this.$store.commit('cart/SET_PIGGY_REWARD_SELECTED', this.reward);
            await this.applyDiscount();
            await this.removeDiscount();

            return {
                ...payload,
                billId: this.billId,
                venueId: this.venueId,
                amount: this.totalPiggyDiscount
            };
        },
        toggleReward(reward) {
            if (!this.isRewardAvailableForCart(reward)) {
                this.$notify({
                    group: 'PiggyRewards',
                    title: this.$t('piggy.addValidItemFirst')
                });

                return;
            }

            if (this.isRewardActive(reward)) {
                this.$store.commit('cart/CLEAR_PIGGY_DISCOUNT');
                this.$store.commit('cart/SET_PIGGY_REWARD_SELECTED', {});
                this.cartTotalWithDiscount();
            } else {
                this.claim(reward);
            }
        },
        isRewardActive(reward) {
            if (this.isBillPayment) {
                return;
            }

            return reward.uuid === this.rewardSelected.uuid;
        }
    }
};
</script>

<style>
.horizontal-list {
    display: flex;
    flex-direction: row;
    flex-wrap: nowrap;
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
    gap: 0.5rem;
}

.horizontal-list li {
    min-width: 210px;
    border: 1px solid #e5e7eb;
    border-radius: 0.5rem;
    padding: 0.5rem;
    flex-shrink: 0;
}

/* Gradient fade for horizontal list only */
.relative:has(.horizontal-list)::after {
    content: '';
    position: absolute;
    top: 0;
    right: 0;
    width: 25px;
    height: 100%;
    pointer-events: none;
    background: linear-gradient(
        to right,
        transparent 0%,
        var(--color-bg-secondary, #ffffff) 80%,
        var(--color-bg-secondary, #ffffff) 100%
    );
    z-index: 1;
}
</style>
